import { GlButton, GlSprintf, GlLink, GlAlert, GlFormCheckbox } from '/builds/gitlab/gitlab-ee/node_modules/@gitlab/ui/src/index.js?vue3';
import { __ } from '~/locale';
import { mergeUrlParams } from '~/lib/utils/url_utility';
import { clearDraft } from '~/lib/utils/autosave';
import MarkdownEditor from '/builds/gitlab/gitlab-ee/app/assets/javascripts/vue_shared/components/markdown/markdown_editor.vue?vue3';
import { trackSavedUsingEditor } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/vue_shared/components/markdown/tracking.js?vue3';
import { COMMENT_FORM } from '~/notes/i18n';
import { createAlert } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/alert.js?vue3';
import { getNoteFormErrorMessages } from '~/notes/utils';
import { CODE_SUGGESTIONS_OFF } from '~/rapid_diffs/utils/code_suggestions';
export default {
    name: 'NoteForm',
    i18n: COMMENT_FORM,
    components: {
        MarkdownEditor,
        GlButton,
        GlSprintf,
        GlLink,
        GlAlert,
        GlFormCheckbox
    },
    inject: {
        endpoints: {
            type: Object
        },
        noteableType: {
            type: String
        },
        codeSuggestions: {
            default: null
        }
    },
    props: {
        noteBody: {
            type: String,
            required: false,
            default: ''
        },
        noteId: {
            type: [
                String,
                Number
            ],
            required: false,
            default: undefined
        },
        saveNote: {
            type: Function,
            required: true
        },
        saveDraft: {
            type: Function,
            required: false,
            default: null
        },
        hasDrafts: {
            type: Boolean,
            required: false,
            default: false
        },
        requestLastNoteEditing: {
            type: Function,
            required: false,
            default: undefined
        },
        saveButtonTitle: {
            type: String,
            required: false,
            default: __('Save comment')
        },
        internal: {
            type: Boolean,
            required: false,
            default: false
        },
        autosaveKey: {
            type: String,
            required: false,
            default: ''
        },
        autofocus: {
            type: Boolean,
            required: false,
            default: true
        },
        canCancel: {
            type: Boolean,
            required: false,
            default: true
        },
        restoreFromAutosave: {
            type: Boolean,
            required: false,
            default: false
        },
        saveNoteErrorMessages: {
            type: Object,
            required: false,
            default: null
        },
        showResolveDiscussionToggle: {
            type: Boolean,
            required: false,
            default: false
        },
        discussionResolved: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: [
        'cancel',
        'handle-suggest-dismissed',
        'input'
    ],
    data () {
        return {
            editedNoteBody: this.noteBody,
            conflictWhileEditing: false,
            isSubmitting: false,
            autocompleteDataSources: gl.GfmAutoComplete?.dataSources,
            isResolving: false,
            isUnresolving: true
        };
    },
    computed: {
        codeSuggestionsConfig () {
            return this.codeSuggestions?.() ?? CODE_SUGGESTIONS_OFF;
        },
        renderMarkdownPath () {
            const { previewParams } = this.codeSuggestionsConfig;
            if (!previewParams) return this.endpoints.previewMarkdown;
            return mergeUrlParams(previewParams, this.endpoints.previewMarkdown);
        },
        draftButtonTitle () {
            return this.hasDrafts ? __('Add to review') : __('Start a review');
        },
        commentButtonTitle () {
            return this.saveDraft ? __('Add comment now') : this.saveButtonTitle;
        },
        commentButtonCategory () {
            return this.saveDraft ? 'secondary' : 'primary';
        },
        isSubmitDisabled () {
            return !this.editedNoteBody.length || this.isSubmitting;
        },
        formFieldProps () {
            return {
                id: 'note_note',
                name: 'note[note]',
                'aria-label': __('Reply to comment'),
                placeholder: this.internal ? this.$options.i18n.bodyPlaceholderInternal : this.$options.i18n.bodyPlaceholder,
                class: 'note-textarea js-gfm-input js-note-text markdown-area js-vue-issue-note-form',
                'data-testid': 'reply-field'
            };
        }
    },
    watch: {
        noteBody () {
            if (this.editedNoteBody === this.noteBody) return;
            this.conflictWhileEditing = true;
        }
    },
    methods: {
        // eslint-disable-next-line vue/no-unused-properties -- public method
        append (value) {
            this.$refs.markdownEditor.append(value);
        },
        editMyLastNote () {
            if (!this.requestLastNoteEditing || this.editedNoteBody !== '') return;
            if (this.requestLastNoteEditing()) this.cancel();
        },
        cancel (shouldConfirm = false) {
            // prevent closing the form when trying to close autocomplete
            if (this.$refs.form.querySelector('textarea.at-who-active')) return;
            clearDraft(this.autosaveKey);
            this.$emit('cancel', shouldConfirm && this.noteBody !== this.editedNoteBody);
        },
        handleKeySubmit (forceUpdate = false) {
            if (this.isSubmitting) return;
            if (this.saveDraft && !forceUpdate) {
                this.handleDraftSubmit();
            } else {
                this.handleUpdate();
            }
        },
        newResolvedState () {
            return this.discussionResolved && !this.isUnresolving || !this.discussionResolved && this.isResolving;
        },
        shouldToggleResolved () {
            if (!this.showResolveDiscussionToggle) return false;
            return this.newResolvedState() !== this.discussionResolved;
        },
        async handleUpdate () {
            this.isSubmitting = true;
            const shouldResolve = this.shouldToggleResolved();
            trackSavedUsingEditor(this.$refs.markdownEditor.isContentEditorActive, `${this.noteableType}_note`);
            try {
                await this.saveNote(this.editedNoteBody, shouldResolve);
                this.editedNoteBody = '';
                clearDraft(this.autosaveKey);
            } catch (error) {
                createAlert({
                    message: getNoteFormErrorMessages(error?.response ?? error, this.saveNoteErrorMessages),
                    parent: this.$el,
                    error
                });
            } finally{
                this.isSubmitting = false;
            }
        },
        async handleDraftSubmit () {
            if (!this.saveDraft) return;
            this.isSubmitting = true;
            const resolveDiscussion = this.showResolveDiscussionToggle ? this.newResolvedState() : false;
            trackSavedUsingEditor(this.$refs.markdownEditor.isContentEditorActive, `${this.noteableType}_note`);
            try {
                await this.saveDraft(this.editedNoteBody, resolveDiscussion);
                this.editedNoteBody = '';
                clearDraft(this.autosaveKey);
            } catch (error) {
                createAlert({
                    message: getNoteFormErrorMessages(error?.response ?? error, this.saveNoteErrorMessages),
                    parent: this.$el,
                    error
                });
            } finally{
                this.isSubmitting = false;
            }
        }
    }
};
